import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Phone, BookOpen, Scale, ExternalLink, ArrowRight, Heart, Users, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

const ResourcesPage = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-warm-800 text-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight leading-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }} data-testid="resources-hero-title">
              Resources
            </h1>
            <p className="text-warm-200 text-lg">
              Crisis hotlines, local support organizations, education materials, and ways to get involved.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Crisis Hotlines */}
      <section className="py-16" data-testid="crisis-hotlines">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-medium text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Crisis Hotlines
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: "National DV Hotline", number: "1-800-799-7233", note: "24/7. TTY: 1-800-787-3224", url: "https://www.thehotline.org", urgent: true },
              { name: "Human Trafficking Hotline", number: "1-888-373-7888", note: "Text 233733", url: "https://humantraffickinghotline.org" },
              { name: "RAINN (Sexual Assault)", number: "1-800-656-4673", note: "24/7 Confidential", url: "https://www.rainn.org" },
              { name: "Suicide & Crisis Lifeline", number: "988", note: "Call or text 988", url: "https://988lifeline.org" },
              { name: "Maryland Crisis Line", number: "211", note: "Statewide resource referral" },
              { name: "Child Abuse Hotline", number: "1-800-422-4453", note: "Childhelp National Hotline" },
            ].map((hotline) => (
              <div key={hotline.name} className={`rounded-xl p-5 border ${hotline.urgent ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">{hotline.name}</h3>
                  {hotline.url && (
                    <a href={hotline.url} target="_blank" rel="noopener noreferrer" className="text-warm-600 hover:text-warm-800">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <a href={`tel:${hotline.number}`} className={`text-xl font-bold block mb-1 ${hotline.urgent ? 'text-red-800' : 'text-warm-800'}`}>
                  {hotline.number}
                </a>
                <p className="text-xs text-gray-500">{hotline.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Support */}
      <section className="py-16 bg-gray-50" data-testid="local-support">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-medium text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Local Support Organizations (Baltimore)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: "House of Ruth Maryland", desc: "Emergency shelter, legal services, counseling for DV survivors", phone: "410-889-0840", url: "https://hruth.org" },
              { name: "TurnAround Inc", desc: "Sexual assault and DV crisis services, hotline, legal advocacy", phone: "443-279-0379", url: "https://www.turnaroundinc.org" },
              { name: "Legal Aid Bureau", desc: "Free legal representation for low-income Marylanders", phone: "410-539-5340", url: "https://www.mdlab.org" },
              { name: "Baltimore City DSS", desc: "Social services, CPS, emergency assistance programs", phone: "443-423-6300" },
              { name: "ROAR Center", desc: "Forensic interview and advocacy center for abuse victims", phone: "", url: "" },
              { name: "Maryland Network Against Domestic Violence", desc: "Statewide coalition, policy advocacy, training", phone: "", url: "https://www.mnadv.org" },
            ].map((org) => (
              <div key={org.name} className="bg-white border border-gray-100 rounded-xl p-5 flex items-start gap-4">
                <Shield className="w-5 h-5 text-warm-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold text-gray-900 text-sm">{org.name}</h3>
                    {org.url && (
                      <a href={org.url} target="_blank" rel="noopener noreferrer" className="text-warm-600 hover:text-warm-800">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{org.desc}</p>
                  {org.phone && <a href={`tel:${org.phone}`} className="text-sm font-medium text-warm-700 mt-1 block">{org.phone}</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Training */}
      <section className="py-16" data-testid="education-materials">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-medium text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Education & Training
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "Survivor Education Programs", desc: "Free workforce readiness and hospitality/ServSafe pathways for survivors rebuilding their lives. Medical assistant, phlebotomy, entrepreneurship, and content creation are on our expansion roadmap.", link: "/services", cta: "View Programs" },
              { icon: Scale, title: "Know Your Rights", desc: "Information on protective orders, victim rights in Maryland, housing protections, and immigration relief options for survivors of violence.", link: "/contact", cta: "Contact Us" },
              { icon: Users, title: "For Professionals", desc: "Resources for caseworkers, law enforcement, and agencies on trauma-informed practices, LAP protocols, and referral processes.", link: "/partner/login", cta: "Partner Portal" },
            ].map((item) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} className="bg-white border border-gray-100 rounded-xl p-6">
                <item.icon className="w-8 h-8 text-warm-700 mb-4" />
                <h3 className="text-base font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{item.desc}</p>
                <Link to={item.link}>
                  <Button variant="outline" size="sm" className="rounded-full text-warm-700 border-warm-300 hover:bg-warm-50">
                    {item.cta} <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 bg-gray-50" data-testid="get-involved">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-medium text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Get Involved
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/donate" className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-md transition-shadow">
              <Heart className="w-8 h-8 text-warm-700 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Donate</h3>
              <p className="text-sm text-gray-600">Support survivors with a tax-deductible donation to our 501(c)(3) organization.</p>
            </Link>
            <Link to="/careers" className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-md transition-shadow">
              <Users className="w-8 h-8 text-warm-700 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Volunteer / Careers</h3>
              <p className="text-sm text-gray-600">Join our team. We need advocates, volunteers, and professionals committed to this work.</p>
            </Link>
            <a href="mailto:director@thepillarprojectbaltimore.org" className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-md transition-shadow">
              <Mail className="w-8 h-8 text-warm-700 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Partner With Us</h3>
              <p className="text-sm text-gray-600">Agencies, businesses, and organizations can establish referral pipelines and co-deliver services.</p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-warm-800 text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Need immediate help?
          </h2>
          <p className="text-warm-200 mb-6">You are not alone. Reach out to us or call a crisis hotline now.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-8 py-5 font-semibold">
                Get Support <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1-800-799-7233">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-5">
                1-800-799-7233
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
